import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Button, Badge, Spinner, Alert, Form, ProgressBar, Modal } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import { colors } from '../../brand-colors';

// IP Inventory categories
const IP_CATEGORIES = [
  {
    key: 'books',
    label: 'Books/eBooks',
    description: 'Published books, eBooks, or guides',
    placeholder: 'List your published works...'
  },
  {
    key: 'frameworks',
    label: 'Named Frameworks/Methodologies',
    description: 'Documented approaches with names (e.g., "The XYZ Method")',
    placeholder: 'List your named frameworks...'
  },
  {
    key: 'processes',
    label: 'SOPs/Checklists',
    description: 'Documented standard operating procedures',
    placeholder: 'List your documented processes...'
  },
  {
    key: 'courses',
    label: 'Courses/Training Programs',
    description: 'Online courses, workshops, certification programs',
    placeholder: 'List your training programs...'
  },
  {
    key: 'presentations',
    label: 'Signature Talks/Presentations',
    description: 'Recurring presentations you give',
    placeholder: 'List your signature presentations...'
  },
  {
    key: 'articles',
    label: 'Published Articles/Content',
    description: 'Blog posts, articles, videos that explain your expertise',
    placeholder: 'Describe your content library...'
  },
  {
    key: 'tools',
    label: 'Proprietary Tools/Templates',
    description: 'Calculators, templates, assessments you\'ve created',
    placeholder: 'List your tools and templates...'
  }
];

function AuthorityAudit() {
  const navigate = useNavigate();
  const [stage, setStage] = useState('intro'); // intro, visibility, inventory, gaps, complete
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Visibility check state
  const [visibilityLoading, setVisibilityLoading] = useState(false);
  const [visibilityResults, setVisibilityResults] = useState(null);
  const [profileInfo, setProfileInfo] = useState(null);

  // Inventory state
  const [inventory, setInventory] = useState({});

  // Gap analysis state
  const [gapAnalysis, setGapAnalysis] = useState(null);

  // Website scraping state
  const [scraping, setScraping] = useState(false);
  const [scrapedData, setScrapedData] = useState(null);

  // Correction/flagging state
  const [showCorrectionModal, setShowCorrectionModal] = useState(false);
  const [correctionData, setCorrectionData] = useState({
    search_type: '',
    incorrect_text: '',
    correction_type: 'not_me',
    correction_note: ''
  });
  const [corrections, setCorrections] = useState([]);
  const [savingCorrection, setSavingCorrection] = useState(false);

  // Existing data
  const [existingAudit, setExistingAudit] = useState(null);

  // Mind to Method import state
  const [m2mImported, setM2mImported] = useState(null);

  useEffect(() => {
    loadExistingData();
    checkAndImportMindToMethod();
    loadCorrections();
  }, []);

  // Load existing corrections
  const loadCorrections = async () => {
    try {
      const response = await fetch('/api/authority-engineer/audit/corrections', {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setCorrections(data.corrections);
        }
      }
    } catch (err) {
      console.error('Error loading corrections:', err);
    }
  };

  // Open correction modal for a specific result
  const openCorrectionModal = (searchType, resultText) => {
    // Get selected text if any
    const selection = window.getSelection();
    const selectedText = selection.toString().trim();

    setCorrectionData({
      search_type: searchType,
      incorrect_text: selectedText || '',
      correction_type: 'not_me',
      correction_note: ''
    });
    setShowCorrectionModal(true);
  };

  // Save correction
  const saveCorrection = async () => {
    if (!correctionData.incorrect_text.trim()) {
      setError('Please select or enter the incorrect text');
      return;
    }

    setSavingCorrection(true);
    try {
      const response = await fetch('/api/authority-engineer/audit/corrections', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(correctionData)
      });

      const data = await response.json();
      if (data.success) {
        setShowCorrectionModal(false);
        loadCorrections();
        setCorrectionData({
          search_type: '',
          incorrect_text: '',
          correction_type: 'not_me',
          correction_note: ''
        });
      } else {
        setError(data.error || 'Failed to save correction');
      }
    } catch (err) {
      setError('Failed to save correction');
    } finally {
      setSavingCorrection(false);
    }
  };

  // Get corrections for a specific search type
  const getCorrectionsForType = (searchType) => {
    return corrections.filter(c => c.search_type === searchType);
  };

  // Auto-import Mind to Method data if emails match
  const checkAndImportMindToMethod = async () => {
    try {
      const response = await fetch('/api/authority-engineer/import-mind-to-method', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' }
      });
      if (response.ok) {
        const data = await response.json();
        if (data.success && data.imported) {
          setM2mImported(data);
          // Reload existing data to pick up the new framework
          loadExistingData();
        }
      }
    } catch (err) {
      console.error('Error checking Mind to Method data:', err);
    }
  };

  // Reload profile info whenever entering visibility stage
  useEffect(() => {
    if (stage === 'visibility') {
      loadProfileInfo();
    }
  }, [stage]);

  // Scrape website for IP inventory
  const scrapeWebsiteForIP = async () => {
    setScraping(true);
    setError('');

    try {
      const response = await fetch('/api/authority-engineer/audit/scrape-ip', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' }
      });

      const data = await response.json();

      if (data.success) {
        // Pre-populate inventory with scraped data
        setInventory(prev => ({
          ...prev,
          ...data.inventory
        }));
        setScrapedData(data);
      } else {
        setError(data.error || 'Failed to scan website');
      }
    } catch (err) {
      setError('Failed to scan website. Please try again.');
    } finally {
      setScraping(false);
    }
  };

  const loadProfileInfo = async () => {
    try {
      const response = await fetch('/api/authority-engineer/audit/profile-preview', {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setProfileInfo(data.profile);
        }
      }
    } catch (err) {
      console.error('Error loading profile:', err);
    }
  };

  const loadExistingData = async () => {
    try {
      const response = await fetch('/api/authority-engineer/audit/complete', {
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success && data.hasData) {
          setExistingAudit(data);
          if (data.audit.status === 'complete') {
            setStage('complete');
          }
          if (data.visibility) {
            setVisibilityResults({
              results: {
                name: { result: data.visibility.nameStatus },
                company: { result: data.visibility.companyStatus },
                industry: { result: data.visibility.industryStatus }
              },
              analysis: {
                name_status: data.visibility.nameStatus,
                company_status: data.visibility.companyStatus,
                industry_status: data.visibility.industryStatus,
                listed_among_experts: data.visibility.listedAmongExperts,
                overall_score: data.visibility.overallScore,
                summary: data.visibility.summary
              }
            });
          }
          if (data.audit.inventory) {
            setInventory(data.audit.inventory);
          }
          if (data.audit.gapAnalysis) {
            setGapAnalysis({
              gap_analysis: data.audit.gapAnalysis,
              undocumented_expertise: data.audit.undocumented,
              priority_recommendations: data.audit.recommendations
            });
          }
        }
      }
    } catch (err) {
      console.error('Error loading existing data:', err);
    }
  };

  const runVisibilityCheck = async () => {
    setVisibilityLoading(true);
    setError('');

    try {
      const response = await fetch('/api/authority-engineer/audit/visibility-check', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' }
      });

      const data = await response.json();

      if (data.success) {
        setVisibilityResults(data);
      } else {
        setError(data.error || 'Failed to run visibility check');
      }
    } catch (err) {
      setError('Failed to run visibility check. Please try again.');
    } finally {
      setVisibilityLoading(false);
    }
  };

  const handleInventoryChange = (key, value) => {
    setInventory(prev => ({ ...prev, [key]: value }));
  };

  const saveInventory = async () => {
    setLoading(true);
    setError('');

    try {
      const response = await fetch('/api/authority-engineer/audit/inventory', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ inventory })
      });

      const data = await response.json();

      if (data.success) {
        setStage('gaps');
        generateGapAnalysis();
      } else {
        setError(data.error || 'Failed to save inventory');
      }
    } catch (err) {
      setError('Failed to save inventory. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const generateGapAnalysis = async () => {
    setLoading(true);
    setError('');

    try {
      const response = await fetch('/api/authority-engineer/audit/generate-gaps', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' }
      });

      const data = await response.json();

      if (data.success) {
        setGapAnalysis(data.analysis);
        setStage('complete');
      } else {
        setError(data.error || 'Failed to generate analysis');
      }
    } catch (err) {
      setError('Failed to generate analysis. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'cited':
      case 'expert_listed':
        return '✅';
      case 'mentioned':
        return '⚠️';
      default:
        return '❌';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'cited':
        return 'Cited as Expert';
      case 'expert_listed':
        return 'Listed Among Experts';
      case 'mentioned':
        return 'Mentioned';
      default:
        return 'Not Found';
    }
  };

  // Render intro stage
  if (stage === 'intro') {
    return (
      <Container fluid className="py-4 px-4">
        <div className="mb-4">
          <Button variant="link" className="p-0 mb-2" onClick={() => navigate('/command-center/authority-engineer')}>
            ← Back to Authority Engineer
          </Button>
          <h1>Authority Audit</h1>
          <p className="text-muted">Discover what AI knows about you and inventory your existing IP</p>
        </div>

        <Row className="justify-content-center">
          <Col lg={8}>
            <Card className="shadow">
              <Card.Body className="p-5">
                <h2 className="text-center mb-4">Welcome to Your Authority Audit</h2>

                <p className="text-center text-muted mb-4">
                  This audit will help you understand your current authority positioning and identify opportunities.
                </p>

                <div className="mb-4">
                  <h5>What We'll Do:</h5>
                  <div className="d-flex align-items-start mb-3">
                    <Badge bg="primary" className="me-3 mt-1">1</Badge>
                    <div>
                      <strong>AI Visibility Check</strong>
                      <p className="text-muted mb-0 small">
                        See what AI platforms (like ChatGPT, Claude, Perplexity) know about you and your business.
                      </p>
                    </div>
                  </div>
                  <div className="d-flex align-items-start mb-3">
                    <Badge bg="primary" className="me-3 mt-1">2</Badge>
                    <div>
                      <strong>IP Inventory</strong>
                      <p className="text-muted mb-0 small">
                        Catalog your existing intellectual property - books, frameworks, courses, presentations, etc.
                      </p>
                    </div>
                  </div>
                  <div className="d-flex align-items-start mb-3">
                    <Badge bg="primary" className="me-3 mt-1">3</Badge>
                    <div>
                      <strong>Gap Analysis</strong>
                      <p className="text-muted mb-0 small">
                        AI will analyze your inventory and identify opportunities to strengthen your authority.
                      </p>
                    </div>
                  </div>
                </div>

                {m2mImported && (
                  <Alert variant="success" className="mb-4">
                    <strong>✨ Good news!</strong> We found your <strong>"{m2mImported.framework_name}"</strong> from
                    the Mind to Method lead magnet and automatically imported it to your IP Library!
                  </Alert>
                )}

                <Alert variant="info">
                  <strong>Why This Matters:</strong> AI platforms are becoming the new search engines.
                  When prospects ask AI "who's the best at X?", will your name come up?
                </Alert>

                <div className="text-center mt-4">
                  <Button
                    size="lg"
                    style={{ backgroundColor: colors.greyDarkest, border: 'none' }}
                    onClick={() => setStage('visibility')}
                  >
                    Start My Audit
                  </Button>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    );
  }

  // Render visibility check stage
  if (stage === 'visibility') {
    return (
      <Container fluid className="py-4 px-4">
        <div className="mb-4">
          <Button variant="link" className="p-0 mb-2" onClick={() => setStage('intro')}>
            ← Back
          </Button>
          <h1>AI Visibility Check</h1>
          <p className="text-muted">See what AI platforms know about you</p>
        </div>

        <ProgressBar now={33} className="mb-4" style={{ height: '8px' }} />

        <Row className="justify-content-center">
          <Col lg={10}>
            {error && <Alert variant="danger" onClose={() => setError('')} dismissible>{error}</Alert>}

            {!visibilityResults ? (
              <Card className="shadow">
                <Card.Body className="p-5 text-center">
                  {visibilityLoading ? (
                    <>
                      <Spinner animation="border" style={{ color: colors.goldMid }} className="mb-3" />
                      <h4>Querying AI Platforms...</h4>
                      <p className="text-muted">
                        This takes about 30-60 seconds. We're asking AI what it knows about you.
                      </p>
                    </>
                  ) : (
                    <>
                      <h4 className="mb-4">Ready to Check Your AI Visibility?</h4>

                      {/* Show what will be searched */}
                      <Alert variant="info" className="text-start mb-4" style={{ maxWidth: '500px', margin: '0 auto' }}>
                        <strong>We'll search for:</strong>
                        {profileInfo ? (
                          <>
                            <ul className="mb-0 mt-2">
                              <li><strong>Name:</strong> {profileInfo.name || <em className="text-muted">Not set</em>}</li>
                              <li><strong>Business:</strong> {profileInfo.business || <em className="text-muted">Not set</em>}</li>
                              <li><strong>Industry:</strong> {profileInfo.industry || <em className="text-muted">Not set</em>}</li>
                            </ul>
                            {(!profileInfo.name || !profileInfo.business || !profileInfo.industry) && (
                              <div className="mt-2 small">
                                <em>Missing info? Update your profile in the Profit Map foundation steps.</em>
                              </div>
                            )}
                          </>
                        ) : (
                          <div className="mt-2 text-muted">Loading profile info...</div>
                        )}
                      </Alert>

                      <p className="text-muted mb-4">
                        We'll run 3 AI searches to see what platforms like ChatGPT and Claude know about you.
                      </p>
                      <Button
                        size="lg"
                        style={{ backgroundColor: colors.greyDarkest, border: 'none' }}
                        onClick={runVisibilityCheck}
                      >
                        Run Visibility Check
                      </Button>
                    </>
                  )}
                </Card.Body>
              </Card>
            ) : (
              <>
                <Card className="shadow mb-4">
                  <Card.Header style={{ backgroundColor: colors.greyDarkest, color: colors.goldLight }}>
                    <h5 className="mb-0">Your AI Visibility Results</h5>
                  </Card.Header>
                  <Card.Body>
                    <Row className="mb-4">
                      <Col md={4} className="text-center border-end">
                        <div style={{ fontSize: '2.5rem' }}>
                          {getStatusIcon(visibilityResults.analysis.name_status)}
                        </div>
                        <strong>Personal Recognition</strong>
                        <div className="text-muted small">
                          {getStatusLabel(visibilityResults.analysis.name_status)}
                        </div>
                      </Col>
                      <Col md={4} className="text-center border-end">
                        <div style={{ fontSize: '2.5rem' }}>
                          {getStatusIcon(visibilityResults.analysis.company_status)}
                        </div>
                        <strong>Company Recognition</strong>
                        <div className="text-muted small">
                          {getStatusLabel(visibilityResults.analysis.company_status)}
                        </div>
                      </Col>
                      <Col md={4} className="text-center">
                        <div style={{ fontSize: '2.5rem' }}>
                          {visibilityResults.analysis.listed_among_experts ? '✅' : '❌'}
                        </div>
                        <strong>Listed Among Experts</strong>
                        <div className="text-muted small">
                          {visibilityResults.analysis.listed_among_experts ? 'Yes!' : 'Not yet'}
                        </div>
                      </Col>
                    </Row>

                    <Alert variant={visibilityResults.analysis.overall_score > 50 ? 'success' : 'warning'}>
                      <strong>Score: {visibilityResults.analysis.overall_score}/100</strong>
                      <p className="mb-0 mt-2">{visibilityResults.analysis.summary}</p>
                    </Alert>

                    {/* Detailed Results */}
                    <h5 className="mt-4 mb-3">Detailed Results:</h5>
                    {visibilityResults.results.name && (
                      <Card className="mb-3" style={{ backgroundColor: '#f8f9fa' }}>
                        <Card.Body className="py-3 px-4">
                          <div className="d-flex justify-content-between align-items-start mb-2">
                            <strong className="text-primary" style={{ fontSize: '1.1rem' }}>
                              Query: "{visibilityResults.results.name.query}"
                            </strong>
                            <Button
                              variant="outline-warning"
                              size="sm"
                              onClick={() => openCorrectionModal('name', visibilityResults.results.name.result)}
                            >
                              Flag Incorrect
                            </Button>
                          </div>
                          {visibilityResults.results.name.verification_note && (
                            <Alert
                              variant={visibilityResults.results.name.is_correct_person ? 'success' : 'warning'}
                              className="py-2 mb-2"
                            >
                              <strong>Identity Check:</strong> {visibilityResults.results.name.verification_note}
                            </Alert>
                          )}
                          {getCorrectionsForType('name').length > 0 && (
                            <Alert variant="info" className="py-2 mb-2">
                              <strong>You flagged:</strong> {getCorrectionsForType('name').length} item(s) as incorrect
                            </Alert>
                          )}
                          <div className="markdown-content" style={{ fontSize: '1rem', lineHeight: '1.7' }}>
                            <ReactMarkdown>{visibilityResults.results.name.result}</ReactMarkdown>
                          </div>
                        </Card.Body>
                      </Card>
                    )}
                    {visibilityResults.results.company && visibilityResults.results.company.query && (
                      <Card className="mb-3" style={{ backgroundColor: '#f8f9fa' }}>
                        <Card.Body className="py-3 px-4">
                          <div className="d-flex justify-content-between align-items-start mb-2">
                            <strong className="text-primary" style={{ fontSize: '1.1rem' }}>
                              Query: "{visibilityResults.results.company.query}"
                            </strong>
                            <Button
                              variant="outline-warning"
                              size="sm"
                              onClick={() => openCorrectionModal('company', visibilityResults.results.company.result)}
                            >
                              Flag Incorrect
                            </Button>
                          </div>
                          {getCorrectionsForType('company').length > 0 && (
                            <Alert variant="info" className="py-2 mb-2">
                              <strong>You flagged:</strong> {getCorrectionsForType('company').length} item(s) as incorrect
                            </Alert>
                          )}
                          <div className="markdown-content" style={{ fontSize: '1rem', lineHeight: '1.7' }}>
                            <ReactMarkdown>{visibilityResults.results.company.result}</ReactMarkdown>
                          </div>
                        </Card.Body>
                      </Card>
                    )}
                    {visibilityResults.results.industry && visibilityResults.results.industry.query && (
                      <Card className="mb-3" style={{ backgroundColor: '#f8f9fa' }}>
                        <Card.Body className="py-3 px-4">
                          <div className="d-flex justify-content-between align-items-start mb-2">
                            <strong className="text-primary" style={{ fontSize: '1.1rem' }}>
                              Query: "{visibilityResults.results.industry.query}"
                            </strong>
                            <Button
                              variant="outline-warning"
                              size="sm"
                              onClick={() => openCorrectionModal('industry', visibilityResults.results.industry.result)}
                            >
                              Flag Incorrect
                            </Button>
                          </div>
                          {getCorrectionsForType('industry').length > 0 && (
                            <Alert variant="info" className="py-2 mb-2">
                              <strong>You flagged:</strong> {getCorrectionsForType('industry').length} item(s) as incorrect
                            </Alert>
                          )}
                          <div className="markdown-content" style={{ fontSize: '1rem', lineHeight: '1.7' }}>
                            <ReactMarkdown>{visibilityResults.results.industry.result}</ReactMarkdown>
                          </div>
                        </Card.Body>
                      </Card>
                    )}
                  </Card.Body>
                </Card>

                <div className="d-flex justify-content-between">
                  <Button variant="outline-secondary" onClick={runVisibilityCheck} disabled={visibilityLoading}>
                    Run Again
                  </Button>
                  <Button
                    style={{ backgroundColor: colors.greyDarkest, border: 'none' }}
                    onClick={() => setStage('inventory')}
                  >
                    Continue to IP Inventory →
                  </Button>
                </div>
              </>
            )}
          </Col>
        </Row>

        {/* Correction Modal */}
        <Modal show={showCorrectionModal} onHide={() => setShowCorrectionModal(false)}>
          <Modal.Header closeButton style={{ backgroundColor: '#fff3cd', borderBottom: '1px solid #ffc107' }}>
            <Modal.Title>
              <span className="me-2">⚠️</span>
              Flag Incorrect Information
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <p className="text-muted mb-3">
              Select or paste the text that is incorrect. We'll track this and notify you when AI updates their information about you.
            </p>

            <Form.Group className="mb-3">
              <Form.Label><strong>What's wrong?</strong></Form.Label>
              <Form.Select
                value={correctionData.correction_type}
                onChange={(e) => setCorrectionData({ ...correctionData, correction_type: e.target.value })}
              >
                <option value="not_me">This is not me / wrong person</option>
                <option value="wrong_info">Information is incorrect</option>
                <option value="outdated">Information is outdated</option>
                <option value="other">Other issue</option>
              </Form.Select>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label><strong>Incorrect Text</strong></Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                placeholder="Paste or type the incorrect text here..."
                value={correctionData.incorrect_text}
                onChange={(e) => setCorrectionData({ ...correctionData, incorrect_text: e.target.value })}
              />
              <Form.Text className="text-muted">
                Tip: Select text in the results before clicking "Flag Incorrect" to auto-fill this field
              </Form.Text>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label><strong>Notes</strong> <span className="text-muted">(optional)</span></Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                placeholder="What should the correct information be?"
                value={correctionData.correction_note}
                onChange={(e) => setCorrectionData({ ...correctionData, correction_note: e.target.value })}
              />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowCorrectionModal(false)}>
              Cancel
            </Button>
            <Button
              variant="warning"
              onClick={saveCorrection}
              disabled={savingCorrection || !correctionData.incorrect_text.trim()}
            >
              {savingCorrection ? 'Saving...' : 'Flag for Monitoring'}
            </Button>
          </Modal.Footer>
        </Modal>
      </Container>
    );
  }

  // Render inventory stage
  if (stage === 'inventory') {
    return (
      <Container fluid className="py-4 px-4">
        <div className="mb-4">
          <Button variant="link" className="p-0 mb-2" onClick={() => setStage('visibility')}>
            ← Back
          </Button>
          <h1>IP Inventory</h1>
          <p className="text-muted">Catalog your existing intellectual property</p>
        </div>

        <ProgressBar now={66} className="mb-4" style={{ height: '8px' }} />

        <Row className="justify-content-center">
          <Col lg={10}>
            {error && <Alert variant="danger" onClose={() => setError('')} dismissible>{error}</Alert>}

            {/* Scan Website Card */}
            <Card className="shadow mb-4" style={{ backgroundColor: '#f0fff4', border: '1px solid #9ae6b4' }}>
              <Card.Body className="p-4">
                <div className="d-flex align-items-center justify-content-between">
                  <div>
                    <h5 className="mb-1" style={{ color: '#276749' }}>
                      <span className="me-2">🔍</span> Auto-Scan Your Website
                    </h5>
                    <p className="text-muted mb-0">
                      We'll analyze your website and pre-populate your IP inventory automatically.
                    </p>
                  </div>
                  <Button
                    variant="success"
                    onClick={scrapeWebsiteForIP}
                    disabled={scraping}
                    style={{ minWidth: '160px' }}
                  >
                    {scraping ? (
                      <><Spinner size="sm" className="me-2" />Scanning...</>
                    ) : (
                      'Scan My Website'
                    )}
                  </Button>
                </div>
                {scrapedData && (
                  <Alert variant="success" className="mt-3 mb-0">
                    <strong>Found IP on your website!</strong> We've pre-filled the form below.
                    {scrapedData.expertise_themes?.length > 0 && (
                      <div className="mt-2">
                        <strong>Expertise themes detected:</strong> {scrapedData.expertise_themes.join(', ')}
                      </div>
                    )}
                    {scrapedData.authority_signals?.length > 0 && (
                      <div className="mt-1">
                        <strong>Authority signals:</strong> {scrapedData.authority_signals.join(', ')}
                      </div>
                    )}
                  </Alert>
                )}
              </Card.Body>
            </Card>

            <Card className="shadow">
              <Card.Header style={{ backgroundColor: colors.greyDarkest, color: colors.goldLight }}>
                <h5 className="mb-0">What IP Do You Already Have?</h5>
              </Card.Header>
              <Card.Body className="p-4">
                <p className="text-muted mb-4">
                  {scrapedData
                    ? "Review what we found and add anything we missed. Edit any fields as needed."
                    : "List everything you've already documented. Leave blank if you don't have anything in that category."}
                </p>

                {IP_CATEGORIES.map((category, idx) => (
                  <Form.Group key={category.key} className="mb-4">
                    <Form.Label>
                      <strong>{category.label}</strong>
                      <span className="text-muted ms-2 small">— {category.description}</span>
                    </Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={2}
                      placeholder={category.placeholder}
                      value={inventory[category.key] || ''}
                      onChange={(e) => handleInventoryChange(category.key, e.target.value)}
                    />
                  </Form.Group>
                ))}

                <Form.Group className="mb-4">
                  <Form.Label>
                    <strong>Other IP</strong>
                    <span className="text-muted ms-2 small">— Anything else that represents your expertise</span>
                  </Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={2}
                    placeholder="Awards, patents, certifications, notable client work..."
                    value={inventory.other || ''}
                    onChange={(e) => handleInventoryChange('other', e.target.value)}
                  />
                </Form.Group>

                <div className="d-flex justify-content-between mt-4">
                  <Button variant="outline-secondary" onClick={() => setStage('visibility')}>
                    ← Back
                  </Button>
                  <Button
                    style={{ backgroundColor: colors.greyDarkest, border: 'none' }}
                    onClick={saveInventory}
                    disabled={loading}
                  >
                    {loading ? (
                      <><Spinner size="sm" className="me-2" />Analyzing...</>
                    ) : (
                      'Generate Gap Analysis →'
                    )}
                  </Button>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    );
  }

  // Render gaps/loading stage
  if (stage === 'gaps') {
    return (
      <Container fluid className="py-4 px-4">
        <div className="mb-4">
          <h1>Analyzing Your Authority...</h1>
        </div>

        <ProgressBar now={90} className="mb-4" style={{ height: '8px' }} />

        <Row className="justify-content-center">
          <Col lg={8}>
            <Card className="shadow">
              <Card.Body className="p-5 text-center">
                <Spinner animation="border" style={{ color: colors.goldMid }} className="mb-3" />
                <h4>Generating Gap Analysis</h4>
                <p className="text-muted">
                  AI is analyzing your inventory and identifying opportunities...
                </p>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    );
  }

  // Render complete stage
  if (stage === 'complete') {
    return (
      <Container fluid className="py-4 px-4">
        <div className="mb-4">
          <Button variant="link" className="p-0 mb-2" onClick={() => navigate('/command-center/authority-engineer')}>
            ← Back to Authority Engineer
          </Button>
          <h1>Authority Audit Complete</h1>
          <p className="text-muted">Your baseline assessment is ready</p>
        </div>

        <ProgressBar now={100} variant="success" className="mb-4" style={{ height: '8px' }} />

        {error && <Alert variant="danger" onClose={() => setError('')} dismissible>{error}</Alert>}

        <Row>
          {/* AI Visibility Summary */}
          <Col lg={6} className="mb-4">
            <Card className="shadow h-100">
              <Card.Header style={{ backgroundColor: colors.greyDarkest, color: colors.goldLight }}>
                <h5 className="mb-0">AI Visibility Score</h5>
              </Card.Header>
              <Card.Body>
                {visibilityResults ? (
                  <>
                    <div className="text-center mb-4">
                      <div style={{
                        width: '120px',
                        height: '120px',
                        borderRadius: '50%',
                        border: `6px solid ${visibilityResults.analysis.overall_score > 50 ? '#28a745' : '#ffc107'}`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        margin: '0 auto'
                      }}>
                        <span style={{ fontSize: '2rem', fontWeight: 'bold' }}>
                          {visibilityResults.analysis.overall_score}
                        </span>
                      </div>
                      <small className="text-muted">out of 100</small>
                    </div>

                    <div className="text-center mb-3">
                      <div className="d-flex justify-content-center gap-4">
                        <div>
                          {getStatusIcon(visibilityResults.analysis.name_status)}
                          <div className="small text-muted">Personal</div>
                        </div>
                        <div>
                          {getStatusIcon(visibilityResults.analysis.company_status)}
                          <div className="small text-muted">Company</div>
                        </div>
                        <div>
                          {visibilityResults.analysis.listed_among_experts ? '✅' : '❌'}
                          <div className="small text-muted">Expert List</div>
                        </div>
                      </div>
                    </div>

                    <p className="text-muted small text-center">
                      {visibilityResults.analysis.summary}
                    </p>

                    <div className="text-center">
                      <Button variant="outline-primary" size="sm" onClick={runVisibilityCheck} disabled={visibilityLoading}>
                        {visibilityLoading ? 'Checking...' : 'Run New Check'}
                      </Button>
                    </div>
                  </>
                ) : (
                  <div className="text-center">
                    <p className="text-muted">No visibility check run yet</p>
                    <Button onClick={runVisibilityCheck} disabled={visibilityLoading}>
                      Run Visibility Check
                    </Button>
                  </div>
                )}
              </Card.Body>
            </Card>
          </Col>

          {/* Gap Analysis */}
          <Col lg={6} className="mb-4">
            <Card className="shadow h-100">
              <Card.Header style={{ backgroundColor: colors.greyDarkest, color: colors.goldLight }}>
                <h5 className="mb-0">Gap Analysis</h5>
              </Card.Header>
              <Card.Body>
                {gapAnalysis ? (
                  <>
                    <p className="mb-3">{gapAnalysis.gap_analysis}</p>

                    {gapAnalysis.undocumented_expertise && gapAnalysis.undocumented_expertise.length > 0 && (
                      <>
                        <h6 className="mt-4">Undocumented Expertise:</h6>
                        <ul className="small">
                          {gapAnalysis.undocumented_expertise.map((item, idx) => (
                            <li key={idx}>{item}</li>
                          ))}
                        </ul>
                      </>
                    )}
                  </>
                ) : (
                  <p className="text-muted">Complete the inventory to see gap analysis</p>
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Priority Recommendations */}
        {gapAnalysis && gapAnalysis.priority_recommendations && gapAnalysis.priority_recommendations.length > 0 && (
          <Card className="shadow mb-4">
            <Card.Header style={{ backgroundColor: colors.greyDarkest, color: colors.goldLight }}>
              <h5 className="mb-0">Priority Recommendations</h5>
            </Card.Header>
            <Card.Body>
              {gapAnalysis.priority_recommendations.map((rec, idx) => (
                <div key={idx} className="d-flex align-items-start mb-3 pb-3 border-bottom">
                  <Badge
                    bg={rec.impact === 'high' ? 'danger' : rec.impact === 'medium' ? 'warning' : 'secondary'}
                    className="me-3 mt-1"
                  >
                    {rec.priority}
                  </Badge>
                  <div>
                    <strong>{rec.action}</strong>
                    <p className="text-muted mb-0 small">{rec.reason}</p>
                  </div>
                </div>
              ))}
            </Card.Body>
          </Card>
        )}

        {/* Next Steps */}
        <Card className="shadow" style={{ backgroundColor: '#f0fff4', border: '1px solid #9ae6b4' }}>
          <Card.Body className="p-4">
            <h5 className="mb-3" style={{ color: '#276749' }}>What's Next?</h5>
            <p className="mb-3">
              Now that you have your baseline, it's time to start building your authority:
            </p>
            <Row>
              <Col md={4}>
                <Button
                  variant="outline-primary"
                  className="w-100"
                  onClick={() => navigate('/command-center/authority-engineer/extract')}
                >
                  Extract Your First IP →
                </Button>
                <small className="d-block text-center text-muted mt-2">Module 2: Mind to Method</small>
              </Col>
              <Col md={4}>
                <Button
                  variant="outline-secondary"
                  className="w-100"
                  onClick={() => setStage('inventory')}
                >
                  Update Inventory
                </Button>
                <small className="d-block text-center text-muted mt-2">Add newly documented IP</small>
              </Col>
              <Col md={4}>
                <Button
                  variant="outline-secondary"
                  className="w-100"
                  onClick={() => navigate('/command-center/authority-engineer')}
                >
                  Back to Dashboard
                </Button>
                <small className="d-block text-center text-muted mt-2">View all modules</small>
              </Col>
            </Row>
          </Card.Body>
        </Card>
      </Container>
    );
  }

  return null;
}

export default AuthorityAudit;
